import type { SaleRecordRequest } from '@repo/desktop-bridge';
import { PARK_TIME_ZONE } from '@repo/desktop-bridge';
import { z } from 'zod';

/**
 * The visitor's time slot, at a counter that asks for one — the Glass Bridge's
 * (#277). Both payment forms hold it, so it lives here as `cash-given.ts` does,
 * and each screen draws `<TimeSlotFields>`.
 *
 * **Printed, never recorded.** It reaches the tax invoice and nothing else; the
 * API carries no slot and the sale stores none.
 *
 * **Required wherever it is drawn**, and drawn only where the outlet asks and
 * there is something to charge. The fields unregister when they are not drawn,
 * so the schema's rule — a drawn field holds a time — is the setting's rule
 * without the schema having to know the setting.
 */

/** `19:55`, as an `<input type="time">` gives it. */
const TIME_OF_DAY = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

/** How long a window the prefill offers. The cashier can correct either end. */
const SLOT_MINUTES = 5;

const slotTime = (message: string) =>
  z
    .string()
    .refine((value) => TIME_OF_DAY.test(value), { message })
    .optional();

/** The two fields, spread into a payment schema. Absent while not drawn. */
export const timeSlotFields = {
  slotFrom: slotTime('Enter when the slot starts.'),
  slotTo: slotTime('Enter when the slot ends.'),
};

/** The park's time of day — Munnar's, as the paper prints every other time. */
const timeOfDay = new Intl.DateTimeFormat('en-GB', {
  timeZone: PARK_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Now and five minutes on, from the clock the screen was handed. */
export function slotDefaults(now: number): { from: string; to: string } {
  return {
    from: timeOfDay.format(now),
    to: timeOfDay.format(now + SLOT_MINUTES * 60_000),
  };
}

/** The slot as the till is asked to print it, where the fields were drawn. */
export function timeSlotOnRequest(values: {
  slotFrom?: string | undefined;
  slotTo?: string | undefined;
}): Pick<SaleRecordRequest, 'timeSlot'> {
  return values.slotFrom === undefined || values.slotTo === undefined
    ? {}
    : { timeSlot: { from: values.slotFrom, to: values.slotTo } };
}
